// ============================================================================
// APPLICATION HOOKS
// ============================================================================
// React hooks for Application Management API operations

import { useState, useEffect, useCallback } from 'react';
import { applicationService } from 'api/services';
import type {
  ApplicationResponse,
  CreateApplicationRequest,
  UpdateApplicationRequest,
  ID,
} from 'api/types';

/**
 * Hook to fetch all applications
 */
export const useApplications = () => {
  const [data, setData] = useState<ApplicationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await applicationService.getAll();
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  return {
    data,
    loading,
    error,
    refetch: fetchApplications,
  };
};

/**
 * Hook to fetch a single application by ID
 */
export const useApplication = (id: ID) => {
  const [data, setData] = useState<ApplicationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchApplication = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await applicationService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchApplication();
  }, [fetchApplication]);

  return {
    data,
    loading,
    error,
    refetch: fetchApplication,
  };
};

/**
 * Hook to create a new application
 */
export const useCreateApplication = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createApplication = async (
    data: CreateApplicationRequest,
    options?: {
      onSuccess?: (app: ApplicationResponse) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await applicationService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createApplication,
    loading,
    error,
    success,
  };
};

/**
 * Hook to update an application
 */
export const useUpdateApplication = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateApplication = async (
    id: ID,
    data: UpdateApplicationRequest,
    options?: {
      onSuccess?: (app: ApplicationResponse) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await applicationService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateApplication,
    loading,
    error,
    success,
  };
};

/**
 * Hook to delete an application
 */
export const useDeleteApplication = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteApplication = async (
    id: ID,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await applicationService.delete(id);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteApplication,
    loading,
    error,
    success,
  };
};
